"use client";

import type { ReactNode } from "react";
import { ChevronLeft, Plus } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";

// // // //

/**
 * Presentational frame for the GlobalChatDock — headers and controls that wrap the
 * list / picker / chat surfaces in both the compact popover and the maximized
 * two-pane modal. Kept separate from the stateful shell so they can be composed
 * and previewed in isolation.
 */

/** Ghost icon button used across the dock frame. */
export function IconButton(props: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
    children: ReactNode;
}) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={props.label}
            title={props.label}
            onClick={props.onClick}
            disabled={props.disabled}
            className={cn(
                "size-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground",
                props.className,
            )}
        >
            {props.children}
        </Button>
    );
}

/** Outlined "+ New" affordance in the list / rail headers. */
export function NewChatButton(props: { onClick: () => void }) {
    return (
        <Button
            variant="outline"
            size="sm"
            onClick={props.onClick}
            aria-label="Start a new chat"
            className="h-6 gap-1.5 rounded-lg border-muted bg-card px-2.5 text-xs font-medium text-primary hover:border-primary-light hover:bg-primary-light/30 hover:text-primary"
        >
            <Plus aria-hidden="true" />
            New
        </Button>
    );
}

/** Header row for a pane: optional back, eyebrow + title + subtitle, trailing controls. */
export function PaneHeader(props: {
    heightClass: string;
    title: string;
    /** Small brand/context line rendered above the title. */
    eyebrow?: string;
    subtitle?: ReactNode;
    onBack?: () => void;
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                "flex shrink-0 items-center gap-2 border-b border-muted py-2.5 pr-2.5 pl-3",
                props.heightClass,
            )}
        >
            {props.onBack && (
                <IconButton label="Back to chats" onClick={props.onBack}>
                    <ChevronLeft />
                </IconButton>
            )}
            <div className={cn("min-w-0 flex-1", !props.onBack && "pl-1.5")}>
                {props.eyebrow && (
                    <div className="truncate font-mono text-[10px] tracking-wide text-muted-foreground">
                        {props.eyebrow}
                    </div>
                )}
                <div className="truncate text-sm font-semibold text-foreground">
                    {props.title}
                </div>
                {props.subtitle}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
                {props.children}
            </div>
        </div>
    );
}
